import React from 'react';
import { HiSearch, HiX } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showClearButton?: boolean;
  disabled?: boolean;
  debounceMs?: number;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder,
  className,
  size = 'md',
  showClearButton = true,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const sizeClasses = {
    sm: 'h-8 text-sm',
    md: 'h-10 text-sm',
    lg: 'h-12 text-base',
  };

  const iconSizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
  };

  const handleClear = () => {
    onChange('');
  };

  return (
    <div className={classNames('relative', className)}>
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <HiSearch className={classNames('text-gray-400', iconSizeClasses[size])} />
      </div>

      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || t('buttons.search')}
        disabled={disabled}
        className={classNames(
          'block w-full pl-10 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200',
          sizeClasses[size],
          {
            'bg-gray-100 cursor-not-allowed': disabled,
            'bg-white': !disabled,
          }
        )}
      />

      {showClearButton && value && !disabled && (
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
          <button
            type="button"
            onClick={handleClear}
            className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <HiX className={classNames(iconSizeClasses[size])} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
